import { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Typography, RadioButton } from '@weareopenr/component-library';
import { ScheduleSelector } from 'src/components/PublishDetailsView/PublishDetailsView';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes/usePriceSchemes';
import { IPublishSetupViewProps } from '../types';
import ChannelsField from '../ChannelsField/ChannelsField';
import styles from './PublishSetupView.module.scss';

const PublishSetupView = ({
  control,
  register,
  watch,
  menuName,
  mealtimes,
  filteredMealtimes = [],
  publicationData,
  showSchedule,
  eyebrow,
  getDisabledReason,
}: IPublishSetupViewProps): ReactElement => {
  const canEnableMealtimes = filteredMealtimes.length > 0;
  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();

  return (
    <section className={styles['setup']}>
      <div className={styles['heading-group']}>
        {eyebrow && (
          <Typography weight="bold" size="medium" className={styles['eyebrow']}>
            {eyebrow}
          </Typography>
        )}
        <Typography size="xxlarge" className={styles['heading']}>
          Publish <strong>{menuName}</strong>
        </Typography>
      </div>
      {mealtimes && (
        <div className={styles['section']}>
          <Typography weight="bold" size="medium">
            Mealtimes
          </Typography>
          <div className={styles['radios']}>
            <RadioButton register={register} name="mealtimes" value="disabled">
              <span className={styles['radio-label']}>
                <Typography size="small" weight="semi-bold">
                  Disable Mealtimes
                </Typography>
                <Typography size="xsmall" className={styles['subtitle']}>
                  The menu section order will follow that of the &lsquo;All Sections&rsquo; tab
                </Typography>
              </span>
            </RadioButton>
            <RadioButton register={register} name="mealtimes" value="enabled" disabled={!canEnableMealtimes}>
              <span className={styles['radio-label']}>
                <Typography size="small" weight="semi-bold">
                  Enable Mealtimes
                </Typography>
                <Typography size="xsmall" className={styles['subtitle']}>
                  {canEnableMealtimes
                    ? 'Each mealtime added to the mealtime program will be published'
                    : mealtimes.length
                      ? 'Menu sections have not been assigned to Mealtimes'
                      : 'The menu does not contain Mealtimes'}
                </Typography>
              </span>
            </RadioButton>
          </div>
        </div>
      )}

      <div className={styles['section']}>
        <Typography weight="bold" size="medium">
          Pricing
        </Typography>
        <Controller
          name="price-scheme"
          control={control}
          render={({ field }) => (
            <div className={styles['autocomplete']}>
              <Autocomplete
                name="price-scheme"
                label="Price Scheme"
                options={priceSchemes.map((scheme) => ({ value: scheme.id, label: scheme.name }))}
                loading={isPriceSchemesLoading}
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value)}
              />
            </div>
          )}
        />
      </div>

      <ChannelsField control={control} publicationData={publicationData} getDisabledReason={getDisabledReason} />

      {showSchedule && (
        <div className={styles['section']}>
          <ScheduleSelector
            register={register}
            watch={watch}
            title="Publish time & date"
            titleSize="medium"
            titleWeight="bold"
            dateLabel="Start Date & Time"
            nowLabel="Publish now"
            scheduleLabel="Schedule"
            showScheduleName
            showConfirmationCheckbox
            radioContainerClassName={styles['schedule-radios']}
            titleClassName={styles['schedule-title']}
          />
        </div>
      )}
    </section>
  );
};

export default PublishSetupView;
